/**
 * Request and Response Interceptors
 * Provides hooks for intercepting and transforming requests/responses
 */

import { logger } from "../utils/logger";

// APIErrorData type removed as it's not used

/**
 * Request configuration
 */
export interface RequestConfig extends RequestInit {
  url: string;
  requiresAuth?: boolean;
  skipAuth?: boolean; // Override to skip auth even if requiresAuth is true
}

/**
 * Response interceptor function type
 */
export type ResponseInterceptor = (
  response: Response,
  config: RequestConfig
) => Promise<Response> | Response;

/**
 * Request interceptor function type
 */
export type RequestInterceptor = (
  config: RequestConfig
) => Promise<RequestConfig> | RequestConfig;

/**
 * Error interceptor function type
 */
export type ErrorInterceptor = (
  error: Error,
  config: RequestConfig
) => Promise<Error> | Error;

/**
 * Interceptor manager
 */
class InterceptorManager {
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private errorInterceptors: ErrorInterceptor[] = [];

  /**
   * Add a request interceptor
   */
  use(onFulfilled?: RequestInterceptor, onRejected?: ErrorInterceptor): number {
    if (onFulfilled) {
      this.requestInterceptors.push(onFulfilled);
    }
    if (onRejected) {
      this.errorInterceptors.push(onRejected);
    }
    return this.requestInterceptors.length - 1;
  }

  /**
   * Add a response interceptor
   */
  useResponse(
    onFulfilled?: ResponseInterceptor,
    onRejected?: ErrorInterceptor
  ): number {
    if (onFulfilled) {
      this.responseInterceptors.push(onFulfilled);
    }
    if (onRejected) {
      this.errorInterceptors.push(onRejected);
    }
    return this.responseInterceptors.length - 1;
  }

  /**
   * Remove an interceptor by ID
   */
  eject(id: number): void {
    // Simple implementation - could be enhanced with better tracking
    // For now, just log that it's not fully implemented
    logger.warn("Interceptor ejection not fully implemented", {
      interceptorId: id,
    });
  }

  /**
   * Execute request interceptors
   */
  async executeRequestInterceptors(
    config: RequestConfig
  ): Promise<RequestConfig> {
    let finalConfig = config;
    for (const interceptor of this.requestInterceptors) {
      finalConfig = await interceptor(finalConfig);
    }
    return finalConfig;
  }

  /**
   * Execute response interceptors
   */
  async executeResponseInterceptors(
    response: Response,
    config: RequestConfig
  ): Promise<Response> {
    let finalResponse = response;
    for (const interceptor of this.responseInterceptors) {
      finalResponse = await interceptor(finalResponse, config);
    }
    return finalResponse;
  }

  /**
   * Execute error interceptors
   */
  async executeErrorInterceptors(
    error: Error,
    config: RequestConfig
  ): Promise<Error> {
    let finalError = error;
    for (const interceptor of this.errorInterceptors) {
      finalError = await interceptor(finalError, config);
    }
    return finalError;
  }
}

/**
 * Global interceptor manager instance
 */
export const interceptors = new InterceptorManager();

/**
 * Get auth token from storage
 */
function getAuthToken(): string | null {
  return localStorage.getItem("authToken");
}

/**
 * Default request interceptor: Add auth token
 */
interceptors.use(async (config: RequestConfig) => {
  const headers = new Headers(config.headers);

  // Add auth token if required and not skipped
  if (config.requiresAuth && !config.skipAuth) {
    const token = getAuthToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  // Ensure Content-Type is set for requests with body
  if (config.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  return {
    ...config,
    headers,
  };
});

/**
 * Default response interceptor: Log responses
 */
interceptors.useResponse(async (response: Response, config: RequestConfig) => {
  // Log request/response for debugging
  const url = config.url;
  const method = config.method || "GET";
  const status = response.status;
  logger.debug(`[API] ${method} ${url} → ${status}`, {
    method,
    url,
    status,
    statusText: response.statusText,
  });

  return response;
});
